import { SHAPEFILE_DETAILS_LABEL_LAYER } from "constants/mapLayers.constants";

import { safeRemoveLayer } from "components/map/utils/layerOperations";
import { IEditableProjectLayer } from "components/project/layers/hooks";
import { Shapefile } from "components/project/shapefiles/queries";
import { shapefileLayerType } from "components/project/shapefiles/shapefiles.constants";

import { addShapefilePropertyLayer } from "./addShapefilePropertyLayer";

interface IOnShapefilePropertyChanged {
  addLayer;
  checkedLayerKeys: string[];
  currentShapefileFeatureProperty: string;
  currentShapefileIdForFeatureProperty: string;
  lastShapefileFeatureProperty: React.MutableRefObject<string>;
  lastShapefileIdForFeatureProperty: React.MutableRefObject<string>;
  layerList: IEditableProjectLayer[];
  layerZoomSettings?: Record<string, { minzoom?: number; maxzoom?: number }>;
  mapbox: mapboxgl.Map;
  shapefileFeatures;
  shapefileListSorted: Shapefile[];
}

/**
 * Since the shapefile property isn't loading for every shapefile, it's easier to manage the layer on it's own depending on the 'current' selected shapefile/property
 * Otherwise, when managed in the onShapefilesChanged function, it would potentially not trigger the load because the source has already been loaded, but the propety hadn't been set yet
 * And for the shapefile property, the source doesn't need to be updated.
 * @param params
 * @returns
 */
export function onShapefilePropertyChanged(params: IOnShapefilePropertyChanged) {
  const {
    addLayer,
    checkedLayerKeys,
    currentShapefileFeatureProperty,
    layerList,
    layerZoomSettings,
    mapbox,
    shapefileFeatures,
    shapefileListSorted,
    currentShapefileIdForFeatureProperty
  } = params;

  if (
    !shapefileListSorted?.length ||
    !currentShapefileFeatureProperty ||
    !Object.keys(shapefileFeatures)?.length ||
    !currentShapefileIdForFeatureProperty
  ) {
    return;
  }

  const hasShapefilePropertyChanged = !(
    currentShapefileFeatureProperty === params.lastShapefileFeatureProperty.current &&
    currentShapefileIdForFeatureProperty ===
      params.lastShapefileIdForFeatureProperty.current
  );

  const currentLayerName = `${currentShapefileIdForFeatureProperty}${shapefileLayerType.property}`;

  // The && !mapbox.getLayer(currentLayerName) check is to cover the case of the shapefile being unchecked,
  // this way it'll add the property layer back when it's checked again
  if (
    hasShapefilePropertyChanged ||
    (!hasShapefilePropertyChanged && !mapbox.getLayer(currentLayerName))
  ) {
    // Remove the previous shapefile's property layer (only one should be visible at a time)
    const lastLayerName = `${params.lastShapefileIdForFeatureProperty.current}${shapefileLayerType.property}`;
    safeRemoveLayer(mapbox, lastLayerName);

    const shapefile = shapefileListSorted.find(
      (shapefile) =>
        shapefile.projectShapefileId === currentShapefileIdForFeatureProperty ||
        shapefile.shapefileId === currentShapefileIdForFeatureProperty
    );

    if (shapefile && mapbox.getSource(currentShapefileIdForFeatureProperty)) {
      addShapefilePropertyLayer({
        addLayer,
        checkedLayerKeys,
        layerList,
        layerZoomSettings,
        shapefile,
        shapefileFeatureProperty: currentShapefileFeatureProperty,
        shapefileFeatures,
        shapefileId: currentShapefileIdForFeatureProperty,
        mapbox
      });
      params.lastShapefileFeatureProperty.current = currentShapefileFeatureProperty;
      params.lastShapefileIdForFeatureProperty.current =
        currentShapefileIdForFeatureProperty;
    }
  } else {
    // Update visibility based on checkedLayerKeys (source of truth for UI toggles)
    const isLabelsLayerChecked = checkedLayerKeys.includes(SHAPEFILE_DETAILS_LABEL_LAYER);
    if (mapbox.getLayer(currentLayerName)) {
      mapbox.setLayoutProperty(
        currentLayerName,
        "visibility",
        isLabelsLayerChecked ? "visible" : "none"
      );
    }
  }
}
